import React, { useState, useEffect } from 'react';
import { Wallet, AlertCircle, CheckCircle, Loader, ChevronDown } from 'lucide-react';
import { useWagmiWallet } from '../hooks/useWagmiWallet';

const WagmiWalletStatus = () => {
  const { 
    isConnected, 
    account, 
    isConnecting,
    currentNetwork, 
    isCorrectNetwork,
    networkError,
    connectWallet,
    disconnect,
    switchToNetwork,
    isConnectPending,
    isSwitchPending,
    connectError,
    availableConnectors
  } = useWagmiWallet();

  const [showWalletOptions, setShowWalletOptions] = useState(false);

  const handleConnect = async (connectorId = 'metaMask') => {
    try {
      await connectWallet(connectorId);
      setShowWalletOptions(false);
    } catch (error) {
      console.error('Failed to connect:', error);
    }
  };

  const toggleWalletOptions = () => {
    setShowWalletOptions(!showWalletOptions);
  };

  // Close wallet options when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showWalletOptions && !event.target.closest('.wallet-dropdown-container')) {
        setShowWalletOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWalletOptions]);

  const handleDisconnect = () => {
    disconnect();
  };

  const handleSwitchNetwork = async (networkKey) => {
    try {
      await switchToNetwork(networkKey);
    } catch (error) {
      console.error('Failed to switch network:', error);
    }
  };

  if (isConnecting || isConnectPending) {
    return (
      <div className="flex items-center space-x-2 text-blue-400">
        <Loader className="w-4 h-4 animate-spin" />
        <span>Connecting wallet...</span>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="flex items-center space-x-3">
        <div className="flex items-center space-x-2 text-gray-400">
          <Wallet className="w-4 h-4" />
          <span>Wallet not connected</span>
        </div>
        
        <div className="relative wallet-dropdown-container z-[9999]">
          <button
            onClick={toggleWalletOptions}
            disabled={isConnectPending}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 disabled:opacity-50"
          >
            {isConnectPending ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Wallet className="w-4 h-4" />
            )}
            <span>Connect Wallet</span>
            <ChevronDown className="w-4 h-4" />
          </button>
          
          {showWalletOptions && (
            <div className="absolute top-full mt-2 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-[9999] min-w-[250px] right-0">
              <div className="py-2">
                {availableConnectors && availableConnectors.length > 0 ? (
                  availableConnectors.map((connector) => {
                    // Enhanced connector display names for wallet extensions only
                    const getDisplayName = (connector) => {
                      if (connector.id === 'metaMask') return 'MetaMask';
                      if (connector.id === 'rabby') return 'Rabby Wallet';
                      if (connector.id === 'trustWallet') return 'Trust Wallet';
                      if (connector.id === 'coinbaseWallet') return 'Coinbase Wallet';
                      if (connector.id === 'injected') return 'Browser Extension Wallet';
                      return connector.name;
                    };
                    
                    const getWalletDesc = (connector) => {
                      if (connector.id === 'metaMask') return 'MetaMask extension';
                      if (connector.id === 'rabby') return 'Rabby Wallet extension';
                      if (connector.id === 'trustWallet') return 'Trust Wallet extension';
                      if (connector.id === 'coinbaseWallet') return 'Coinbase Wallet extension';
                      return 'Browser wallet extension';
                    };

                    return (
                      <button
                        key={connector.id}
                        onClick={() => handleConnect(connector.id)}
                        disabled={isConnectPending}
                        className="w-full text-left px-4 py-3 text-white hover:bg-gray-700 flex items-start space-x-3 disabled:opacity-50"
                      >
                        <Wallet className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{getDisplayName(connector)}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{getWalletDesc(connector)}</div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="px-4 py-3 text-center">
                    <div className="text-gray-300 font-medium mb-2">No wallet detected</div>
                    <div className="text-xs text-gray-400 mb-3">
                      Please install a browser extension wallet to use this bridge.
                    </div>
                    <div className="text-xs text-blue-300 space-y-1">
                      <div className="font-medium mb-1">Recommended wallets:</div>
                      <div>• <a href="https://metamask.io" target="_blank" rel="noopener noreferrer" className="hover:text-blue-200 underline">MetaMask</a></div>
                      <div>• <a href="https://rabby.io" target="_blank" rel="noopener noreferrer" className="hover:text-blue-200 underline">Rabby Wallet</a></div>
                      <div>• <a href="https://trustwallet.com" target="_blank" rel="noopener noreferrer" className="hover:text-blue-200 underline">Trust Wallet</a></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {connectError && (
          <div className="text-red-400 text-sm max-w-xs">
            {connectError.message}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-3">
      <div className="flex items-center space-x-2">
        {isCorrectNetwork ? (
          <CheckCircle className="w-4 h-4 text-green-400" />
        ) : (
          <AlertCircle className="w-4 h-4 text-yellow-400" />
        )}
        <div className="text-sm">
          <div className="text-white">
            {account ? account : 'Connected'}
          </div>
          <div className="text-gray-400">
            {currentNetwork ? `${currentNetwork.color} ${currentNetwork.name}` : 'Unknown Network'}
          </div>
        </div>
      </div>
      
      {!isCorrectNetwork && currentNetwork && (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleSwitchNetwork('bscMainnet')}
            disabled={isSwitchPending}
            className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded text-sm disabled:opacity-50 flex items-center space-x-1"
          >
            <img src="/bsc.svg" alt="BSC" className="w-3 h-3" />
            <span>Switch to BSC</span>
          </button>
          <button
            onClick={() => handleSwitchNetwork('xphereMainnet')}
            disabled={isSwitchPending}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm disabled:opacity-50 flex items-center space-x-1"
          >
            <img src="/xpt.svg" alt="Xphere" className="w-3 h-3" />
            <span>Switch to Xphere</span>
          </button>
        </div>
      )}
      
      <button
        onClick={handleDisconnect}
        className="text-gray-400 hover:text-white text-sm"
      >
        Disconnect
      </button>
      
      {networkError && (
        <div className="text-red-400 text-sm max-w-xs truncate" title={networkError}>
          {networkError}
        </div>
      )}
    </div>
  );
};

export default WagmiWalletStatus;
